import CalendarMonth from "@mui/icons-material/CalendarMonth";
import Delete from "@mui/icons-material/Delete";
import Draw from "@mui/icons-material/Draw";
import ExpandMore from "@mui/icons-material/ExpandMore";
import GroupAdd from "@mui/icons-material/GroupAdd";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Send from "@mui/icons-material/Send";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import Tune from "@mui/icons-material/Tune";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from "@mui/material";
import {
  BulkAddReviewersDialog,
  BulkSetSignerDialog,
  BulkActionSubmission,
} from "components/BulkActions";
import { ChangeEncampStatusDialog } from "components/Submission/ChangeEncampStatusDialog";
import { VALID_ENCAMP_STATUSES } from "encamp-shared/src/submissions";
import { Frequency } from "generated-graphql/graphql";
import { useMemo, useState } from "react";

import { ChangeAssigneeDialog } from "./ChangeAssigneeDialog";
import { ChangeDueDateDialog } from "./ChangeDueDateDialog";
import { ChangeStatusDialog } from "./ChangeStatusDialog";
import { DeleteSubmissionsConfirmationDialog } from "./DeleteSubmissionsConfirmationDialog";
import { SendToReviewersDialog } from "./SendToReviewersDialog";
import { Row } from "./types";

/**
 * Transforms Submissions grid Row to common BulkActionSubmission format.
 */
function toBulkActionSubmission(row: Row): BulkActionSubmission {
  return {
    id: row.id,
    submissionId: row.submission?.id ?? null,
    isVirtualEvent: row.frequency !== Frequency.Once,
    status: row.submission?.status ?? null,
    dueDate: row.deadline ?? null,
    facilityName: row.facilities?.map((f: { name: string }) => f.name).join(", ") || "",
    submissionType: row.submission?.submissionType ?? null,
    title: row.title ?? null,
    reviewerCount: row.submission?.reviewers?.length ?? 0,
  };
}

type SubmissionBulkActionsMenuProps = {
  submissions: Row[];
  onClearSelections?: () => void;
};

export function SubmissionBulkActionsMenu({
  submissions,
  onClearSelections,
}: SubmissionBulkActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showChangeAssigneeDialog, setShowChangeAssigneeDialog] = useState(false);
  const [showChangeDueDateDialog, setShowChangeDueDateDialog] = useState(false);
  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);
  const [showSendToReviewersDialog, setShowSendToReviewersDialog] = useState(false);
  const [showDeleteSubmissionsConfirmationDialog, setShowDeleteSubmissionsConfirmationDialog] =
    useState(false);
  const [showBulkAddReviewersDialog, setShowBulkAddReviewersDialog] = useState(false);
  const [showBulkSetSignerDialog, setShowBulkSetSignerDialog] = useState(false);
  const [showChangeEncampStatusDialog, setShowChangeEncampStatusDialog] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenChangeAssignee = () => {
    handleClose();
    setShowChangeAssigneeDialog(true);
  };

  const handleOpenChangeDueDate = () => {
    handleClose();
    setShowChangeDueDateDialog(true);
  };

  const handleOpenChangeStatus = () => {
    handleClose();
    setShowChangeStatusDialog(true);
  };

  const handleOpenSendToReviewers = () => {
    handleClose();
    setShowSendToReviewersDialog(true);
  };

  const handleOpenDeleteSubmissionsConfirmation = () => {
    handleClose();
    setShowDeleteSubmissionsConfirmationDialog(true);
  };

  const handleOpenBulkAddReviewers = () => {
    handleClose();
    setShowBulkAddReviewersDialog(true);
  };

  const handleOpenBulkSetSigner = () => {
    handleClose();
    setShowBulkSetSignerDialog(true);
  };

  const { canChangeEncampStatus, encampStatusSubmissions } = useMemo(() => {
    const materialized = submissions.filter(
      (r) => r.submission?.id != null && r.frequency === Frequency.Once
    );
    if (materialized.length === 0) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const statuses = new Set(materialized.map((r) => r.submission?.status));
    if (statuses.size !== 1) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const commonStatus = [...statuses][0];
    const hasValidSubStatuses =
      !!commonStatus && (VALID_ENCAMP_STATUSES[commonStatus]?.length ?? 0) > 0;
    return {
      canChangeEncampStatus: hasValidSubStatuses,
      encampStatusSubmissions: materialized.flatMap((r) => {
        const sub = r.submission;
        if (!sub) return [];
        return [
          { submissionId: sub.id, submissionStatus: sub.status, encampStatus: sub.encampStatus },
        ];
      }),
    };
  }, [submissions]);

  const handleOpenChangeEncampStatus = () => {
    handleClose();
    setShowChangeEncampStatusDialog(true);
  };

  const handleSuccess = () => {
    onClearSelections?.();
  };

  return (
    <>
      <Button
        id="bulk-actions-button"
        aria-controls={open ? "bulk-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={<ExpandMore />}
        variant="contained"
        color="secondary"
        disabled={submissions.length === 0}
        sx={{ textTransform: "uppercase" }}
      >
        Bulk Actions
      </Button>
      <Menu
        id="bulk-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "bulk-actions-button",
        }}
      >
        <MenuList>
          <MenuItem onClick={handleOpenChangeStatus}>
            <ListItemIcon>
              <SwapHoriz fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change status...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeEncampStatus} disabled={!canChangeEncampStatus}>
            <ListItemIcon>
              <Tune fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change Encamp Status...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeAssignee}>
            <ListItemIcon>
              <PersonAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change assignee(s)...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeDueDate}>
            <ListItemIcon>
              <CalendarMonth fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change due date...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenBulkAddReviewers}>
            <ListItemIcon>
              <GroupAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add reviewers...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenBulkSetSigner}>
            <ListItemIcon>
              <Draw fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set signer...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenSendToReviewers}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send to reviewers...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenDeleteSubmissionsConfirmation}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete selected submissions...</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      <ChangeStatusDialog
        open={showChangeStatusDialog}
        onClose={() => setShowChangeStatusDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <ChangeEncampStatusDialog
        open={showChangeEncampStatusDialog}
        onClose={() => setShowChangeEncampStatusDialog(false)}
        submissions={encampStatusSubmissions}
        onSuccess={handleSuccess}
      />

      <ChangeAssigneeDialog
        open={showChangeAssigneeDialog}
        onClose={() => setShowChangeAssigneeDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <ChangeDueDateDialog
        open={showChangeDueDateDialog}
        onClose={() => setShowChangeDueDateDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <SendToReviewersDialog
        open={showSendToReviewersDialog}
        onClose={() => setShowSendToReviewersDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <DeleteSubmissionsConfirmationDialog
        open={showDeleteSubmissionsConfirmationDialog}
        onClose={() => setShowDeleteSubmissionsConfirmationDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <BulkAddReviewersDialog
        open={showBulkAddReviewersDialog}
        onClose={() => setShowBulkAddReviewersDialog(false)}
        submissions={submissions.map(toBulkActionSubmission)}
        onSuccess={handleSuccess}
        refetchQueries={["SubmissionVirtualEvents"]}
      />

      <BulkSetSignerDialog
        open={showBulkSetSignerDialog}
        onClose={() => setShowBulkSetSignerDialog(false)}
        submissions={submissions.map(toBulkActionSubmission)}
        onSuccess={handleSuccess}
        refetchQueries={["SubmissionVirtualEvents"]}
      />
    </>
  );
}
